import React, { useState, useEffect } from 'react';
import { ChevronLeft, Shield, AlertCircle } from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS, getAuthHeaders } from '../config/api.ts';

interface Login2FAProps {
  onBack: () => void;
  onSuccess: () => void;
  onReset?: () => void; // 2FA sıfırlandığında çağrılacak callback
  email: string;
}

export default function Login2FA({ onBack, onSuccess, onReset, email }: Login2FAProps) {
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [secret, setSecret] = useState<string | null>(null);

  useEffect(() => {
    // Backend'den secret'ı al
    loadSecret();
  }, [email]);

  const loadSecret = async () => {
    const token = localStorage.getItem('adminToken');
    let backendSecret = null;
    
    // Önce backend'den secret'ı almayı dene
    if (token) {
      try {
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.STAFF_2FA_STATUS}`, {
          method: 'GET',
          headers: getAuthHeaders(token),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.secret) {
            backendSecret = data.secret;
            console.log('✅ 2FA secret backend\'den alındı');
          }
        } else {
          console.warn('⚠️ Backend 2FA status endpoint hatası:', response.status);
        }
      } catch (error) {
        // Backend endpoint yok veya erişilemiyor
        console.warn('⚠️ Backend 2FA endpoint bulunamadı, localStorage kullanılıyor');
      }
    }

    // Backend'den secret alındıysa onu kullan
    if (backendSecret) {
      setSecret(backendSecret);
      return;
    }

    // Fallback: localStorage'dan al
    const localSecret = localStorage.getItem(`twoFactorSecret_${email}`);
    if (localSecret) {
      setSecret(localSecret);
      console.warn('⚠️ 2FA secret localStorage\'dan alındı. Backend endpoint\'leri oluşturulmalı.');
    } else {
      setError('2FA kurulumu bulunamadı. Lütfen tekrar kurulum yapın.');
    }
  };

  const verifyCode = async () => {
    if (!code || code.length !== 6) {
      setError('Lütfen 6 haneli kodu girin');
      return;
    }

    if (!secret) {
      setError('2FA kurulumu bulunamadı. Lütfen tekrar kurulum yapın.');
      return;
    }

    setIsVerifying(true);
    setError('');

    const token = localStorage.getItem('adminToken');
    
    // Önce backend'de doğrula (eğer endpoint varsa)
    if (token) {
      try {
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.STAFF_2FA_VERIFY}`, {
          method: 'POST',
          headers: getAuthHeaders(token),
          body: JSON.stringify({ code }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            // Backend'de doğrulandı
            onSuccess();
            setIsVerifying(false);
            return;
          } else {
            setError(data.message || 'Geçersiz kod. Lütfen Authenticator uygulamanızdan gelen güncel 6 haneli kodu girin.');
            setIsVerifying(false);
            return;
          }
        }
      } catch (error) {
        // Backend hatası - fallback olarak frontend'de doğrula
        console.log('Backend 2FA endpoint bulunamadı, frontend doğrulama kullanılıyor');
      }
    }
    
    // Fallback: Frontend'de doğrula
    // Dynamic import kullan (TypeScript için)
    const otplib = await import('@otplib/preset-default');
    const authenticator = otplib.authenticator as any; // TypeScript tip tanımları eksik
    
    try {
      const verified = authenticator.verify({
        token: code,
        secret: secret,
        window: 2
      });

      setTimeout(() => {
        if (verified) {
          onSuccess();
        } else {
          setError('Geçersiz kod. Lütfen Authenticator uygulamanızdan gelen güncel 6 haneli kodu girin. Kod her 30 saniyede değişir.');
        }
        setIsVerifying(false);
      }, 1000);
    } catch (error) {
      setTimeout(() => {
        setError('Kod doğrulama hatası. Lütfen tekrar deneyin.');
        setIsVerifying(false);
      }, 1000);
    }
  };


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    verifyCode();
  };

  const reset2FA = async () => {
    // Backend'den de secret'ı sil
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        // Backend'de 2FA'yı devre dışı bırak
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.STAFF_2FA_DISABLE}`, {
          method: 'POST',
          headers: getAuthHeaders(token),
        });
        
        if (response.ok) {
          console.log('✅ 2FA backend\'den sıfırlandı');
        } else {
          console.warn('⚠️ Backend 2FA sıfırlama hatası:', response.status);
        }
      } catch (error) {
        console.warn('⚠️ Backend 2FA endpoint bulunamadı, sadece localStorage temizleniyor:', error);
      }
    }
    
    // Remove 2FA secret for this user from localStorage
    localStorage.removeItem(`twoFactorSecret_${email}`);
    localStorage.removeItem('2FASetup');
    
    // Eğer onReset callback'i varsa onu çağır (Setup2FA modalını açmak için)
    if (onReset) {
      onReset();
    } else {
      // Fallback: Go back to login
      onBack();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 my-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-800"
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Geri
          </button>
          <div className="flex items-center">
            <Shield className="w-6 h-6 text-blue-600 mr-2" />
            <h1 className="text-xl font-bold text-gray-900">2FA Doğrulama</h1>
          </div>
        </div>

        {/* Content */}
        <div className="text-center">
          <div className="mb-6">
            <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              İki Faktörlü Doğrulama
            </h2>
            <p className="text-gray-600">
              {email} için Authenticator uygulamanızdan 6 haneli kodu girin
            </p>
            <p className="text-sm text-gray-500 mt-2">
              GitHub gibi çalışır: Bir kez QR kod okuttuktan sonra, Authenticator'dan sürekli değişen kodları kullanırsınız.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mb-4">
            <div className="mb-4">
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full text-center text-2xl font-mono tracking-widest py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={6}
                autoFocus
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  {error}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isVerifying || code.length !== 6}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50 transition-all"
            >
              {isVerifying ? 'Doğrulanıyor...' : 'Doğrula'}
            </button>
          </form>

          {/* Reset option for users who lost their code */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-3">
              Authenticator kodunu bulamıyor musunuz?
            </p>
            <button
              onClick={reset2FA}
              className="w-full bg-red-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-red-700 text-sm transition-all"
            >
              2FA'yı Sıfırla ve Yeni Kurulum Yap
            </button>
          </div>

          {/* Help - Collapsible */}
          <details className="mt-4">
            <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800 font-medium">
              Yardım
            </summary>
            <div className="mt-3 bg-gray-50 rounded-lg p-4 text-left">
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Authenticator uygulamanızı açın (Google Authenticator, Authy, vb.)</li>
                <li>• "PawleyClub Admin" hesabını bulun</li>
                <li>• Güncel 6 haneli kodu girin</li>
                <li>• Kod her 30 saniyede değişir</li>
                <li>• <strong>QR kod okutmanıza gerek yok</strong> - sadece Authenticator'dan kodu girin</li>
              </ul>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}
